import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/extends";
var _excluded = ["fullScreen", "offsetY", "isTouchDeviceLandscapeView", "type"],
  _excluded2 = ["title", "type", "className", "onClose", "buttonDefs", "children", "subTitle", "open", "closeButton", "footerComponent", "headerComponent", "loading", "showLoadingIndicator", "adColumn", "messages", "offsetY", "isTouchDeviceLandscapeView", "headerPrefixIconName", "disableAutoFocus", "data-testid"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useLayoutEffect, useMemo, useRef, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import { ThemeProvider } from '@mui/material/styles';
import Zoom from '@mui/material/Zoom';
import useMediaQuery from '@mui/material/useMediaQuery';
import Dialog from '@mui/material/Dialog';
import Paper from '@mui/material/Paper';
import DialogContent from '@mui/material/DialogContent';
import { visuallyHidden } from '@mui/utils';
import { EmbersLoading } from '../loading/loading';
import { EmbersModalTitle } from './modalTitle';
import { EmbersModalFooter } from './modalFooter';
import { EmbersFeedback } from './feedback';
import { ROOT_PREFIX, CONTENT_ROOT_PREFIX, dialogTheme } from './modalStyles';
import { isPhone } from '../utils/platformUtils';
var TransitionZoom = /*#__PURE__*/React.forwardRef(function Transition(props, ref) {
  return /*#__PURE__*/React.createElement(Zoom, _extends({
    in: true,
    ref: ref
  }, props));
});
var CustomPaperComponent = function CustomPaperComponent(_ref) {
  var fullScreen = _ref.fullScreen,
    _ref$offsetY = _ref.offsetY,
    offsetY = _ref$offsetY === void 0 ? 56 : _ref$offsetY,
    isTouchDeviceLandscapeView = _ref.isTouchDeviceLandscapeView,
    type = _ref.type,
    props = _objectWithoutProperties(_ref, _excluded);
  var PaperRef = useRef(null);
  useLayoutEffect(function () {
    var root = PaperRef === null || PaperRef === void 0 ? void 0 : PaperRef.current;
    if (root) {
      root.setAttribute('aria-modal', 'true');
      root.setAttribute('role', ['transactional', 'destructive', 'warning'].includes(type) ? 'alertdialog' : 'dialog');
      if (fullScreen) {
        root.style.margin = '0';
        if (isTouchDeviceLandscapeView) {
          // on landscape view, the keyboard pop up shouldn't squezee the space, which will cause the Modal height too small
          root.style.maxHeight = "".concat(window.innerHeight, "px");
        } else {
          root.style.maxHeight = '100%';
        }
      } else {
        var scrollArea = root.querySelector('.MuiDialogContent-root');
        var setScrollStyle = function setScrollStyle() {
          if (scrollArea && scrollArea.scrollHeight > scrollArea.clientHeight) {
            root.style.margin = '0';
            root.style.marginTop = "".concat(offsetY, "px");
          }
        };
        if (isTouchDeviceLandscapeView) {
          // on landscape view, the keyboard pop up shouldn't squezee the space, which will cause the Modal height too small
          root.style.maxHeight = "".concat(window.innerHeight - Math.abs(offsetY) - 48, "px");
          setScrollStyle();
        } else {
          root.style.maxHeight = "calc(100% - ".concat(Math.abs(offsetY), "px - 48px)");
          setScrollStyle();
        }
      }
    }
  }, [fullScreen, isTouchDeviceLandscapeView]);
  return /*#__PURE__*/React.createElement(Paper, _extends({}, props, {
    ref: PaperRef
  }));
};
var EmbersModalLoadingIndicator = function EmbersModalLoadingIndicator(_ref2) {
  var loading = _ref2.loading;
  var intl = useIntl();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    readoutLoading = _useState2[0],
    setReadoutLoading = _useState2[1];
  useEffect(function () {
    if (loading) {
      // use setTimeout to delay the readout, to avoid  that it doesn't read out when loading is displayed immediately after mounting the modal
      setTimeout(function () {
        setReadoutLoading(true);
      }, 200);
    } else {
      setReadoutLoading(false);
    }
  }, [loading]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, !!loading && /*#__PURE__*/React.createElement(EmbersLoading, {
    size: "large",
    className: "".concat(CONTENT_ROOT_PREFIX, "-loadingIndicator")
  }), /*#__PURE__*/React.createElement("div", {
    style: visuallyHidden,
    "aria-live": "polite"
  }, readoutLoading ? intl.formatMessage({
    id: 'embers.button.loading',
    defaultMessage: 'Loading'
  }) : ''));
};
export var EmbersModal = function EmbersModal(_ref3) {
  var title = _ref3.title,
    _ref3$type = _ref3.type,
    type = _ref3$type === void 0 ? 'transactional' : _ref3$type,
    className = _ref3.className,
    onClose = _ref3.onClose,
    buttonDefs = _ref3.buttonDefs,
    children = _ref3.children,
    subTitle = _ref3.subTitle,
    open = _ref3.open,
    closeButton = _ref3.closeButton,
    footerComponent = _ref3.footerComponent,
    headerComponent = _ref3.headerComponent,
    loading = _ref3.loading,
    showLoadingIndicator = _ref3.showLoadingIndicator,
    adColumn = _ref3.adColumn,
    messages = _ref3.messages,
    _ref3$offsetY = _ref3.offsetY,
    offsetY = _ref3$offsetY === void 0 ? 56 : _ref3$offsetY,
    _ref3$isTouchDeviceLa = _ref3.isTouchDeviceLandscapeView,
    isTouchDeviceLandscapeView = _ref3$isTouchDeviceLa === void 0 ? false : _ref3$isTouchDeviceLa,
    headerPrefixIconName = _ref3.headerPrefixIconName,
    disableAutoFocus = _ref3.disableAutoFocus,
    _ref3$dataTestid = _ref3['data-testid'],
    dataTestId = _ref3$dataTestid === void 0 ? 'embers-modal' : _ref3$dataTestid,
    props = _objectWithoutProperties(_ref3, _excluded2);
  var intl = useIntl();

  // mobile landscape and portrait should be fullscreen
  var fullScreen = useMediaQuery('(max-width:686px)') || isPhone();
  var showCloseable = closeButton ? closeButton : type === 'error' || type === 'informational' || type === 'feedback';
  var showActions = type !== 'error' && type !== 'informational' && type !== 'feedback';
  var _subTitle = type === 'announcement' ? title || subTitle : subTitle;
  var _headerComponent = type === 'announcement' ? adColumn : headerComponent;
  var _showLoadingIndicator = showLoadingIndicator === undefined ? !showActions : showLoadingIndicator;
  var _ref4 = buttonDefs || {},
    primary = _ref4.primary,
    secondary = _ref4.secondary,
    tertiary = _ref4.tertiary;
  var actions = function actions() {
    var _buttonSettings;
    var defaultPrimary = {
      'data-testid': "".concat(dataTestId, "-primary-button"),
      level: 'primary',
      colorTheme: 'purple',
      label: intl.formatMessage({
        id: 'embers.button.save',
        defaultMessage: 'Save'
      }),
      size: fullScreen ? 'large' : 'medium'
    };
    var defaultSecondary = {
      'data-testid': "".concat(dataTestId, "-secondary-button"),
      level: 'secondary',
      colorTheme: 'purple',
      drawingStyle: 'outlined',
      label: intl.formatMessage({
        id: 'embers.button.cancel',
        defaultMessage: 'Cancel'
      }),
      size: fullScreen ? 'large' : 'medium',
      onClick: onClose
    };
    var primaryConstraintProps = loading ? {
      disabled: true
    } : {};
    var secondaryConstraintProps = loading ? {
      disabled: true
    } : {};
    var tertiaryConstraintProps = loading ? {
      disabled: true
    } : {};
    if (!_showLoadingIndicator) {
      // if loading indicator is not shown, the loading position of button should be center
      // if secondary button is loading, primary button should be disabled, otherwise secondary button should be disabled
      if (secondary !== null && secondary !== void 0 && secondary.loading) {
        primaryConstraintProps = loading ? {
          disabled: true
        } : {};
        secondaryConstraintProps = loading ? {
          loading: true,
          loadingPosition: 'center',
          disabled: false
        } : {};
      } else {
        primaryConstraintProps = loading ? {
          loading: true,
          loadingPosition: 'center',
          disabled: false
        } : {};
        secondaryConstraintProps = loading ? {
          disabled: true
        } : {};
      }
    }
    var buttonSettings = {};
    switch (type) {
      case 'destructive':
        buttonSettings = {
          primary: {
            level: 'primary',
            colorTheme: 'red',
            label: intl.formatMessage({
              id: 'embers.button.destruct',
              defaultMessage: 'Destruct'
            })
          },
          secondary: {
            colorTheme: 'grey'
          }
        };
        break;
      case 'warning':
        buttonSettings = {
          secondary: {
            level: 'secondary',
            colorTheme: 'purple',
            drawingStyle: 'outlined',
            label: intl.formatMessage({
              id: 'embers.button.leave',
              defaultMessage: 'Leave without saving'
            })
          }
        };
        break;
      case 'announcement':
        buttonSettings = {
          tertiary: {
            label: intl.formatMessage({
              id: 'embers.button.neverShow',
              defaultMessage: 'Never show again'
            })
          }
        };
        break;
      default:
        break;
    }
    var modalButtons = {
      primary: _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, defaultPrimary), buttonSettings.primary), primary || {}), primaryConstraintProps),
      secondary: _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, defaultSecondary), buttonSettings.secondary), secondary || {}), secondaryConstraintProps)
    };
    // only create the tertiary one if it is specified, no default
    if ((_buttonSettings = buttonSettings) !== null && _buttonSettings !== void 0 && _buttonSettings.tertiary || tertiary) {
      modalButtons.tertiary = _objectSpread(_objectSpread(_objectSpread({}, buttonSettings.tertiary || {}), tertiary || {}), tertiaryConstraintProps);
    }
    return modalButtons;
  };
  var keyUpHandle = useMemo(function () {
    return function (e) {
      if (open) {
        // esc to trigger close modal
        if (e.keyCode === 27) {
          onClose === null || onClose === void 0 || onClose();
        }
      }
    };
  }, [open, onClose]);
  var Paper = useCallback(function (props) {
    return CustomPaperComponent(_objectSpread(_objectSpread({}, props), {}, {
      fullScreen: fullScreen,
      offsetY: offsetY,
      isTouchDeviceLandscapeView: isTouchDeviceLandscapeView,
      type: type
    }));
  }, [fullScreen, isTouchDeviceLandscapeView]);
  var feedbackDisp;
  if (type === 'feedback') {
    feedbackDisp = /*#__PURE__*/React.createElement(EmbersFeedback, {
      fullScreen: fullScreen,
      messages: messages
    });
  }
  var modalHeader;
  if (_headerComponent) {
    modalHeader = _headerComponent;
  } else {
    modalHeader = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(EmbersModalTitle, {
      title: title,
      type: type,
      closeable: showCloseable,
      onClose: onClose,
      prefixIconName: headerPrefixIconName
    }));
  }
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: dialogTheme
  }, /*#__PURE__*/React.createElement(Dialog, _extends({
    "data-testid": dataTestId,
    open: open,
    onKeyUp: keyUpHandle,
    fullScreen: fullScreen,
    className: classnames("".concat(ROOT_PREFIX, "-").concat(type), _defineProperty(_defineProperty({}, "".concat(ROOT_PREFIX, "-loading"), loading), "".concat(ROOT_PREFIX, "-showLoadingIndicator"), _showLoadingIndicator), className),
    onBackdropClick: onClose,
    TransitionComponent: TransitionZoom,
    transitionDuration: {
      appear: 300,
      enter: 300,
      exit: 1
    },
    PaperComponent: Paper
  }, props), modalHeader, /*#__PURE__*/React.createElement(DialogContent, {
    className: "".concat(CONTENT_ROOT_PREFIX, "-root")
  }, _showLoadingIndicator && /*#__PURE__*/React.createElement(EmbersModalLoadingIndicator, {
    loading: loading
  }), /*#__PURE__*/React.createElement("div", {
    className: "".concat(CONTENT_ROOT_PREFIX, "-content")
  }, _subTitle && /*#__PURE__*/React.createElement("h3", {
    className: "".concat(ROOT_PREFIX, "-subtitle")
  }, _subTitle), children, feedbackDisp)), footerComponent || showActions && /*#__PURE__*/React.createElement(EmbersModalFooter, {
    actionProps: actions(),
    disableAutoFocus: disableAutoFocus
  })));
};